import { Layered } from '@/domain/group-settings'

export type { Layered }

export type ExchangeFlow =
  | 'NO_EXCHANGE_FLOW'
  | 'ONE_ON_ONE'
  | 'FULL'
  | 'OPEN'
  | 'OPEN_WITH_GIFT_CARD'

// Which exchanges shoppers are offered: the General tab.
export interface ExchangePolicy {
  exchangeFlow: ExchangeFlow
  // How much more than the returned item, in cents, a variant shoppers get without paying the difference can cost.
  threshold: number
  destinationScopedStock: boolean
}

export type OrderPrice = 'FULL' | 'ZERO'
export type ShippingLine = 'CHEAPEST' | 'ZERO' | 'ZERO_WITH_ZONE' | 'ORIGINAL'

export interface ExchangeOrders {
  price: OrderPrice
  shippingLine: ShippingLine
  // Empty for "Exchange by REVER". PrestaShop only.
  paymentMethod: '' | 'SAME_AS_ORIGINAL'
  // Shopify only.
  carryOverDiscounts: boolean
}

// The catalog shoppers pick from in a Full Catalog exchange. Shopify only for now.
export interface FullCatalog {
  // Handle of the store menu whose collections shoppers browse.
  menuId: string
  excludedProductIds: string[]
  excludedCollectionIds: string[]
  excludedTags: string[]
  // Hide products not published on the Online Store.
  onlineStoreOnly: boolean
  // The title above a product's extra details, by language. Kept in the portal copies, not in these settings.
  extraTitles?: Record<string, string>
}

export type InstantExchangeFeeType =
  | 'PERCENTAGE'
  | 'FIXED'
  | 'INSTANT_EXCHANGE_FEE_TYPE_UNSPECIFIED'
export type InstantExchangeTiming =
  | 'ON_START_RETURN_PROCESS'
  | 'ON_ITEM_SENT'
  | 'NO_TIMING'

// Shoppers get the new item before sending theirs back, for a fee. Only REVER edits it.
export interface InstantExchange {
  enabled: boolean
  feeType: InstantExchangeFeeType
  percentageFeeValue: number
  // In cents. A cap or floor of 0 means none.
  fixedFeeAmount: number
  feeCapAmount: number
  feeFloorAmount: number
  timing: InstantExchangeTiming
}

export type ExchangesKind = 'policy' | 'orders' | 'catalog' | 'instant'
export type ExchangesSettings =
  | ExchangePolicy
  | ExchangeOrders
  | FullCatalog
  | InstantExchange

export interface GroupExchanges {
  policy: Layered<ExchangePolicy>
  orders: Layered<ExchangeOrders>
  catalog: Layered<FullCatalog>
  instant: Layered<InstantExchange>
  // The portals are on more than one store, so each sets its own Full Catalog.
  multipleStores: boolean
}

export type PriceCover = 'ALWAYS' | 'NEVER' | 'CUSTOM'

// The threshold is an int32: its maximum covers any price difference.
export const ALWAYS_COVERED = 2147483647

// What Custom starts from when picked from Always or Never: the threshold onboarding gives new portals.
export const DEFAULT_CUSTOM_THRESHOLD = 8000

// Free shipping lines, the only ones a zero-price order can have.
export const FREE_SHIPPING_LINES: ShippingLine[] = ['ZERO', 'ZERO_WITH_ZONE']

export const priceCover = (threshold: number): PriceCover =>
  threshold <= 0 ? 'NEVER' : threshold >= ALWAYS_COVERED ? 'ALWAYS' : 'CUSTOM'

export const DEFAULT_POLICY: ExchangePolicy = {
  exchangeFlow: 'NO_EXCHANGE_FLOW',
  threshold: 0,
  destinationScopedStock: false
}

export const DEFAULT_ORDERS: ExchangeOrders = {
  price: 'FULL',
  shippingLine: 'ZERO',
  paymentMethod: '',
  carryOverDiscounts: false
}

export const DEFAULT_FULL_CATALOG: FullCatalog = {
  menuId: '',
  excludedProductIds: [],
  excludedCollectionIds: [],
  excludedTags: [],
  onlineStoreOnly: false
}

export const DEFAULT_INSTANT_EXCHANGE: InstantExchange = {
  enabled: false,
  feeType: 'INSTANT_EXCHANGE_FEE_TYPE_UNSPECIFIED',
  percentageFeeValue: 0,
  fixedFeeAmount: 0,
  feeCapAmount: 0,
  feeFloorAmount: 0,
  timing: 'NO_TIMING'
}
